import DataTable, { Column } from '@/components/CommonComponents/DataTable';
import DeleteModal from '@/components/CommonComponents/DeleteModal';
import Pagination from '@/components/CommonComponents/Pagination';
import { showToast } from '@/helper/toast.helper';
import { desanitizeKey } from '@/helper/utils';
import { usePermission } from '@/hooks/usePermission';
import { projectSystemUserService } from '@/services/adminUserService/projectUserSerice';
import { rolesPermissionsService } from '@/services/rolesPermissionsService/rolesPermissionsService';
import { AppDispatch, RootState } from '@/store';
import { toggleProjectUserStatus } from '@/store/slices/adminUsersSlice/projectSystemUserSlice';
import { getProjectById } from '@/store/slices/projectsSlice/projectsSlice';
import { MetaData, ProjectUser } from '@/types/systemprojectuser.types';
import { X } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

interface ProjectUsersModalProps {
  isOpen: boolean;
  onClose: () => void;
  blueNumberId: string;
  userName?: string;
  userId?: number;
}

const ProjectUsersModal: React.FC<ProjectUsersModalProps> = ({
  isOpen,
  onClose,
  blueNumberId,
  userName,
  userId = 0,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [projectUsers, setProjectUsers] = useState<ProjectUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteUser, setDeleteUser] = useState<ProjectUser | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [metaData, setMetaData] = useState<MetaData | null>(null);
  const [roles, setRoles] = useState<Record<number, string>>({});

  const { selected, loading: projectLoading } = useSelector((state: RootState) => state.projects);

  const permissions = {
    create: usePermission('write', 'users'),
    edit: usePermission('edit', 'users'),
    delete: usePermission('delete', 'users'),
    export: usePermission('export', 'users'),
  };

  const projectUsersColumns: Column<ProjectUser>[] = [
    {
      key: 'phoneNumber',
      header: 'Mobile Number',
      render: (_, row) => `${row.countryCode}${row.phoneNumber}`,
    },
    {
      key: 'roleId',
      header: 'Role',
      render: (roleId) => {
        const roleName = roles[roleId];
        if (!roleName) return <span>N/A</span>;

        const roleColors: Record<string, string> = {
          SUPER_ADMIN: 'bg-[#CC167C] text-[#1A0206]',
          ADMIN: 'bg-[#4CBEE7CC] text-[#1A0206]',
          CONTENT_MODERATOR: 'bg-[#4C86E7CC] text-[#1A0206]',
          VIEWER: 'bg-[#5CC9A7CC] text-[#1A0206]',
          SURVEY_MANAGER: 'bg-[#E86A9BCC] text-[#1A0206]',
        };

        return (
          <span
            className={`flex justify-center items-center w-full px-3 py-1 rounded-2xl text-xs font-medium ${
              roleColors[roleName] || 'bg-gray-200'
            }`}
          >
            {desanitizeKey(roleName)}
          </span>
        );
      },
    },
    {
      key: 'project',
      header: 'Project Name',
      render: (project) => project?.name || 'N/A',
    },
    {
      key: 'assignedAt',
      header: 'Assigned Date',
      render: (value) => value?.split('T')[0] || 'N/A',
    },
    {
      key: 'status',
      header: 'Status',
      render: (status) => (
        <span
          className={`px-2 py-1 rounded text-xs ${
            status ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}
        >
          {status ? 'Active' : 'Inactive'}
        </span>
      ),
    },
  ];

  useEffect(() => {
    if (isOpen && blueNumberId) {
      fetchProjectUsers();
      fetchRoles();
    }
  }, [isOpen, blueNumberId, currentPage, pageSize]);

  const fetchRoles = async () => {
    try {
      const response = await rolesPermissionsService.getAllRolesDropdown();
      const roleMap: Record<number, string> = {};
      response.data?.forEach((role: any) => {
        roleMap[role.id] = role.name;
      });
      setRoles(roleMap);
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const fetchProjectUsers = async () => {
    setLoading(true);
    try {
      const response = await projectSystemUserService.getAll({
        search: blueNumberId,
        currentPage,
        pageSize,
      });
      const usersWithPermissions = (response.data || []).map((user) => ({
        ...user,
        _permissions: permissions,
      }));
      setProjectUsers(usersWithPermissions);
      setMetaData(response.meta);
    } catch (error) {
      console.error('Error fetching project users:', error);
      setProjectUsers([]);
      setMetaData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = useCallback(async () => {
    if (!deleteUser) return;
    try {
      await projectSystemUserService.remove(deleteUser.id);
      showToast.success(`Project user deleted successfully`);
      setProjectUsers((prev) => prev.filter((user) => user.id !== deleteUser.id));
      setIsDeleteOpen(false);
      setDeleteUser(null);
    } catch (error) {
      showToast.error('Failed to delete project user');
    }
  }, [deleteUser]);

  const openDeleteModal = useCallback((user: ProjectUser) => {
    setDeleteUser(user);
    setIsDeleteOpen(true);
  }, []);

  const handleStatusToggle = useCallback(
    async (id: string, newStatus: boolean) => {
      const userId = Number(id);
      const user = projectUsers.find((u) => u.id === userId);

      if (!user) return;

      const oldStatus = user.status;

      setProjectUsers((prev) =>
        prev.map((u) => (u.id === userId ? { ...u, status: newStatus } : u)),
      );

      try {
        await dispatch(toggleProjectUserStatus(userId)).unwrap();
        showToast.success('Status updated successfully');
      } catch (error) {
        setProjectUsers((prev) =>
          prev.map((u) => (u.id === userId ? { ...u, status: oldStatus } : u)),
        );
        showToast.error('Failed to update status');
      }
    },
    [dispatch, projectUsers],
  );

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  }, []);

  const handleViewProject = useCallback(
    async (user: ProjectUser) => {
      try {
        if (!user.project?.id) return;
        const project = await dispatch(getProjectById(String(user.project.id))).unwrap();
        if (!projectLoading) {
          navigate('/dashboard/project-management/IndividualProject', {
            state: { project: project.data },
          });
        }
        onClose();
      } catch (error) {
        showToast.error('Failed to fetch project details');
      }
    },
    [dispatch, navigate, onClose],
  );

  if (!isOpen) return null;

  return (
    <div
      className="fixed left-[255px] top-[72px] right-0 bottom-0 z-40 flex items-center justify-center"
      style={{
        background:
          'linear-gradient(174.07deg, rgba(173, 12, 98, 0.85) 0.38%, rgba(98, 28, 67, 0.85) 95.87%)',
      }}
    >
      <div className="bg-white shadow-xl rounded-lg w-full max-w-7xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#E0C9D8] flex-shrink-0 bg-[#F2EBEE]">
          <h2 className="text-lg font-bold text-[#621C43] truncate pr-4">Project Access List</h2>
          <button
            onClick={onClose}
            className="text-[#4A082D] hover:text-[#AD0C62] transition-colors flex-shrink-0"
          >
            <X size={24} />
          </button>
        </div>

        <div className="flex-1 flex flex-col min-h-0 overflow-hidden px-4">
          <DataTable
            columns={projectUsersColumns}
            data={projectUsers}
            listTitle=""
            loading={loading}
            onView={handleViewProject}
            onDelete={openDeleteModal}
            onStatusToggle={handleStatusToggle}
            permissions={{ delete: true }}
          />

          {metaData && (
            <Pagination
              currentPage={currentPage}
              totalPages={metaData.totalPages}
              pageSize={pageSize}
              onPageSizeChange={handlePageSizeChange}
              onPageChange={handlePageChange}
            />
          )}
        </div>

        <DeleteModal
          isOpen={isDeleteOpen}
          onClose={() => {
            setIsDeleteOpen(false);
            setDeleteUser(null);
          }}
          onDelete={handleDeleteUser}
          pageTitle="Project User"
          // itemName={`User Name ${deleteUser?.blueNumberId}`}
        />
      </div>
    </div>
  );
};

export default ProjectUsersModal;
